import { v } from "convex/values";

// Categorias de notificacion que agrupan tipos de eventos
export const notificationCategory = v.union(
  v.literal("quotations"),
  v.literal("sales"),
  v.literal("billing"),
  v.literal("reports"),
  v.literal("subscriptions"),
  v.literal("operations"),
  v.literal("logistics"),
);

// Preferencia por categoria: toggles de sistema y email
const categoryPreference = v.object({
  system: v.boolean(),
  email: v.boolean(),
});

// Preferencias de notificacion a nivel de usuario
export const notificationPreferencesValidator = v.object({
  // Master toggles por canal
  channels: v.object({
    system: v.boolean(),
    email: v.boolean(),
  }),
  // Toggles por categoria
  quotations: v.optional(categoryPreference),
  sales: v.optional(categoryPreference),
  billing: v.optional(categoryPreference),
  reports: v.optional(categoryPreference),
  subscriptions: v.optional(categoryPreference),
  operations: v.optional(categoryPreference),
  logistics: v.optional(categoryPreference),
  // Legacy field - kept for backward compatibility with existing data
  commercial: v.optional(categoryPreference),
});

// Configuracion de notificaciones a nivel de empresa
export const notificationSettingsValidator = v.object({
  // La empresa puede desactivar email globalmente
  emailEnabled: v.boolean(),
  // Defaults para usuarios nuevos
  defaults: v.object({
    quotations: v.optional(categoryPreference),
    sales: v.optional(categoryPreference),
    billing: v.optional(categoryPreference),
    reports: v.optional(categoryPreference),
    subscriptions: v.optional(categoryPreference),
    operations: v.optional(categoryPreference),
    logistics: v.optional(categoryPreference),
    // Legacy field - kept for backward compatibility with existing data
    commercial: v.optional(categoryPreference),
  }),
});

// Defaults cuando no hay preferencias configuradas
export const DEFAULT_USER_PREFERENCES = {
  channels: { system: true, email: true },
  quotations: { system: true, email: false },
  sales: { system: true, email: false },
  billing: { system: true, email: true },
  reports: { system: true, email: false },
  subscriptions: { system: true, email: true },
  operations: { system: true, email: false },
  logistics: { system: true, email: false },
} as const;

export const DEFAULT_COMPANY_SETTINGS = {
  emailEnabled: true,
  defaults: {
    quotations: { system: true, email: false },
    sales: { system: true, email: false },
    billing: { system: true, email: true },
    reports: { system: true, email: false },
    subscriptions: { system: true, email: true },
    operations: { system: true, email: false },
    logistics: { system: true, email: false },
  },
} as const;

// Mapeo de tipo de notificacion a categoria
export const NOTIFICATION_TYPE_TO_CATEGORY: Record<string, string> = {
  // Operations - Work Orders
  work_order_created: "operations",
  work_order_status_changed: "operations",
  work_order_completed: "operations",
  work_order_assigned: "operations",
  // Quotations
  quotation_created: "quotations",
  quotation_approved: "quotations",
  quotation_expired: "quotations",
  // Sales
  sale_created: "sales",
  sale_updated: "sales",
  sale_cancelled: "sales",
  message_received: "sales",
  // Billing
  invoice_created: "billing",
  invoice_completed: "billing",
  invoice_cancelled: "billing",
  payment_received: "billing",
  payment_overdue: "billing",
  correlative_range_warning: "billing",
  correlative_range_exhausted: "billing",
  correlative_expiration_warning: "billing",
  correlative_expired: "billing",
  credit_limit_warning: "billing",
  credit_overdue: "billing",
  // Reports
  report_ready: "reports",
  report_failed: "reports",
  report_shared: "reports",
  // Subscriptions
  subscription_expired: "subscriptions",
  trial_ending: "subscriptions",
  trial_ended: "subscriptions",
  grace_period_ending: "subscriptions",
  trial_ending_soon: "subscriptions",
  grace_period_reminder: "subscriptions",
  limit_exceeded: "subscriptions",
  limit_warning: "subscriptions",
  // Logistics
  package_received: "logistics",
  package_consolidated: "logistics",
  package_in_transit: "logistics",
  package_delivered: "logistics",
  package_returned: "logistics",
  consolidado_closed: "logistics",
  consolidado_departed: "logistics",
  consolidado_arrived: "logistics",
};

type ChannelPreference = { system: boolean; email: boolean };
type UserPreferences = {
  channels: ChannelPreference;
  quotations?: ChannelPreference;
  sales?: ChannelPreference;
  billing?: ChannelPreference;
  reports?: ChannelPreference;
  subscriptions?: ChannelPreference;
  operations?: ChannelPreference;
  logistics?: ChannelPreference;
  commercial?: ChannelPreference;
};
type CompanySettings = {
  emailEnabled: boolean;
  defaults: {
    quotations?: ChannelPreference;
    sales?: ChannelPreference;
    billing?: ChannelPreference;
    reports?: ChannelPreference;
    subscriptions?: ChannelPreference;
    operations?: ChannelPreference;
    logistics?: ChannelPreference;
    commercial?: ChannelPreference;
  };
};

type CategoryKey = keyof typeof DEFAULT_USER_PREFERENCES;

function resolveUserCategory(
  prefs: UserPreferences,
  category: string,
): ChannelPreference | undefined {
  const direct = prefs[category as keyof UserPreferences] as
    | ChannelPreference
    | undefined;
  if (direct) return direct;

  // Fallback: legacy "commercial" -> quotations/sales
  if ((category === "quotations" || category === "sales") && prefs.commercial) {
    return prefs.commercial;
  }

  return DEFAULT_USER_PREFERENCES[category as CategoryKey];
}

function resolveCompanyCategory(
  settings: CompanySettings,
  category: string,
): ChannelPreference | undefined {
  const direct = settings.defaults[
    category as keyof CompanySettings["defaults"]
  ] as ChannelPreference | undefined;
  if (direct) return direct;

  if (
    (category === "quotations" || category === "sales") &&
    settings.defaults.commercial
  ) {
    return settings.defaults.commercial;
  }

  return DEFAULT_COMPANY_SETTINGS.defaults[
    category as keyof typeof DEFAULT_COMPANY_SETTINGS.defaults
  ];
}

export function shouldNotify(
  notificationType: string,
  channel: "system" | "email",
  userPrefs?: UserPreferences | null,
  companySettings?: CompanySettings | null,
): boolean {
  const category = NOTIFICATION_TYPE_TO_CATEGORY[notificationType];
  if (!category) return channel === "system";

  const prefs = userPrefs ?? DEFAULT_USER_PREFERENCES;
  const company = companySettings ?? DEFAULT_COMPANY_SETTINGS;

  // Company-level email kill switch
  if (channel === "email" && !company.emailEnabled) return false;

  // User master channel toggle
  if (!prefs.channels[channel]) return false;

  // Category-level toggle with legacy fallback
  const categoryPrefs = resolveUserCategory(prefs, category);
  if (!categoryPrefs) return channel === "system";

  return categoryPrefs[channel];
}
